/* eslint-disable react/no-unstable-nested-components */
import PropTypes from "prop-types";

import Grid from "@mui/material/Grid";
import Checkbox from "@mui/material/Checkbox";
import { Field } from "redux-form";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import FormField from "components/FormField";
import { useMaterialUIController } from "context";

const DAILY_UNREAD_CHAT_TEXT_FIELDS = [
  { key: "subject", labelKey: "emailFieldSubjectSuffix" },
  { key: "emailTitle", labelKey: "emailFieldEmailTitle" },
  { key: "emailText", labelKey: "emailFieldEmailText" },
];

const SUPPORTED_LOCALES = ["es", "en"];

const DAILY_UNREAD_CHAT_DEFAULT_TEXTS = {
  es: {
    subject: "Tienes mensajes sin leer",
    emailTitle: "Tienes mensajes sin leer",
    emailText:
      "Te escribimos para avisarte de que tienes uno o varios mensajes sin leer en tu chat con el gestor.",
  },
  en: {
    subject: "You have unread messages",
    emailTitle: "You have unread messages",
    emailText:
      "We are writing to let you know that you have one or more unread messages in your chat with the manager.",
  },
};

const getTextValue = (textsConfig, fieldKey, locale) => {
  const value = textsConfig?.[fieldKey]?.[locale];
  return typeof value === "string" ? value : "";
};

const getDefaultTextPlaceholder = (fieldKey, locale) => {
  const safeLocale = locale === "en" ? "en" : "es";
  return DAILY_UNREAD_CHAT_DEFAULT_TEXTS[safeLocale]?.[fieldKey] || "";
};

const buildTextsByFieldAndLocale = (textsConfig = {}) =>
  Object.fromEntries(
    DAILY_UNREAD_CHAT_TEXT_FIELDS.map((field) => [
      field.key,
      Object.fromEntries(
        SUPPORTED_LOCALES.map((locale) => [locale, getTextValue(textsConfig, field.key, locale)])
      ),
    ])
  );

export const buildDailyUnreadChatPayload = (templateSource = {}) => ({
  enabled: Boolean(templateSource?.enabled),
  texts: buildTextsByFieldAndLocale(templateSource?.texts),
});

function DailyUnreadChatTextFields({ t }) {
  const visibleTextFields = DAILY_UNREAD_CHAT_TEXT_FIELDS;

  return (
    <Grid container spacing={2} sx={{ marginLeft: 5 }}>
      {visibleTextFields.map((field) => (
        <Grid size={{ xs: 12 }} key={field.key}>
          <MDTypography
            variant="button"
            fontWeight="medium"
            mb={1}
            display="block"
            sx={{ textTransform: "uppercase", fontSize: 14, marginTop: 3 }}
          >
            {t(field.labelKey)}
          </MDTypography>
          <Grid container spacing={2}>
            {SUPPORTED_LOCALES.map((locale) => (
              <Grid size={{ xs: 12, md: 6 }} key={locale}>
                <Field
                  name={`emailTemplates.dailyUnreadChat.texts.${field.key}.${locale}`}
                  component={FormField}
                  label={`${t(field.labelKey)} (${locale.toUpperCase()})`}
                  placeholder={getDefaultTextPlaceholder(field.key, locale)}
                  multiline
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      ))}
    </Grid>
  );
}

DailyUnreadChatTextFields.propTypes = {
  t: PropTypes.func.isRequired,
};

function DailyUnreadChatEmailSection({
  isTemplateExpanded,
  onToggleTemplateExpanded,
  t,
  templateInput,
}) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <MDBox display="flex" alignItems="center" gap={1}>
          <Checkbox
            checked={!!templateInput.value}
            onChange={(event) => templateInput.onChange(event.target.checked)}
          />
          <MDTypography
            variant="button"
            fontWeight="medium"
            textTransform="uppercase"
            sx={{
              cursor: "pointer",
              userSelect: "none",
              fontSize: 18,
              color: darkMode ? "#f8fafc" : "#0f172a",
            }}
            onClick={onToggleTemplateExpanded}
          >
            {t("emailDailyUnreadChat")}
          </MDTypography>
        </MDBox>
      </Grid>

      {templateInput.value && isTemplateExpanded && <DailyUnreadChatTextFields t={t} />}
    </>
  );
}

DailyUnreadChatEmailSection.propTypes = {
  isTemplateExpanded: PropTypes.bool.isRequired,
  onToggleTemplateExpanded: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  templateInput: PropTypes.shape({
    value: PropTypes.any,
    onChange: PropTypes.func.isRequired,
  }).isRequired,
};

export default DailyUnreadChatEmailSection;
